import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || '';
      if (msg === 'Maximum Attempt Reached') {
        setError('Maximum Attempt Reached');
      } else if (msg === 'UNAUTHORISED IP DETECTED') {
        setError('UNAUTHORISED IP DETECTED');
      } else {
        setError('ACCESS DENIED');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Enhanced Galaxy with Heavy Animation */}
      <div className="galaxy-background">
        {/* Cosmic Dust Particles */}
        <div className="cosmic-dust">
          <div className="dust-particle dust-1"></div>
          <div className="dust-particle dust-2"></div>
          <div className="dust-particle dust-3"></div>
          <div className="dust-particle dust-4"></div>
          <div className="dust-particle dust-5"></div>
          <div className="dust-particle dust-6"></div>
          <div className="dust-particle dust-7"></div>
          <div className="dust-particle dust-8"></div>
          <div className="dust-particle dust-9"></div>
          <div className="dust-particle dust-10"></div>
        </div>

        {/* Galaxy Spiral */}
        <div className="galaxy-spiral">
          <div className="spiral-arm spiral-1"></div>
          <div className="spiral-arm spiral-2"></div>
          <div className="spiral-arm spiral-3"></div>
        </div>

        {/* Enhanced Star Field */}
        <div className="galaxy-stars">
          <div className="star star-1"></div>
          <div className="star star-2"></div>
          <div className="star star-3"></div>
          <div className="star star-4"></div>
          <div className="star star-5"></div>
          <div className="star star-6"></div>
          <div className="star star-7"></div>
          <div className="star star-8"></div>
          <div className="star star-9"></div>
          <div className="star star-10"></div>
          <div className="star star-11"></div>
          <div className="star star-12"></div>
          <div className="star star-13"></div>
          <div className="star star-14"></div>
          <div className="star star-15"></div>
          <div className="star star-16"></div>
          <div className="star star-17"></div>
          <div className="star star-18"></div>
          <div className="star star-19"></div>
          <div className="star star-20"></div>
        </div>
        
        {/* Constellation Lines */}
        <div className="constellation-lines">
          <div className="constellation-line line-1"></div>
          <div className="constellation-line line-2"></div>
          <div className="constellation-line line-3"></div>
          <div className="constellation-line line-4"></div>
          <div className="constellation-line line-5"></div>
        </div>
        
        {/* Enhanced Orion Constellation */}
        <div className="orion-constellation">
          <div className="orion-star orion-star-1"></div>
          <div className="orion-star orion-star-2"></div>
          <div className="orion-star orion-star-3"></div>
          <div className="orion-star orion-star-4"></div>
          <div className="orion-star orion-star-5"></div>
          <div className="orion-star orion-star-6"></div>
          <div className="orion-star orion-star-7"></div>
        </div>
        
        {/* Enhanced Nebula with More Layers */}
        <div className="galaxy-nebula">
          <div className="nebula-layer nebula-1"></div>
          <div className="nebula-layer nebula-2"></div>
          <div className="nebula-layer nebula-3"></div>
          <div className="nebula-layer nebula-4"></div>
          <div className="nebula-layer nebula-5"></div>
          <div className="nebula-layer nebula-6"></div>
        </div>

        {/* Cosmic Waves */}
        <div className="cosmic-waves">
          <div className="cosmic-wave wave-1"></div>
          <div className="cosmic-wave wave-2"></div>
          <div className="cosmic-wave wave-3"></div>
        </div>

        {/* Enhanced Shooting Stars */}
        <div className="shooting-stars">
          <div className="shooting-star shooting-star-1"></div>
          <div className="shooting-star shooting-star-2"></div>
          <div className="shooting-star shooting-star-3"></div>
          <div className="shooting-star shooting-star-4"></div>
          <div className="shooting-star shooting-star-5"></div>
        </div>

        {/* Pulsing Star Clusters */}
        <div className="star-clusters">
          <div className="star-cluster cluster-1"></div>
          <div className="star-cluster cluster-2"></div>
          <div className="star-cluster cluster-3"></div>
        </div>
      </div>
      
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.png" alt="Admin" className="login-logo" />
          <p>Sign in to access the admin dashboard</p>
          <div className="admin-only-notice">
            <span className="admin-badge">🔒 Admin Only</span>
            <p>Only users with admin privileges can access this dashboard</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button type="submit" disabled={isLoading} className="login-button">
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
